<template>
    <h2 class="display-sans-serif">{{ getLabel() }}</h2>
    <div class="mt-6 mb-10 max-w-4xl">
        <AnalyticsFilters ref="analyticsFiltersRef" :show-k-anonymization-error="hasKAnonError" @updated="filters = $event" @is-fetching-updated="isFetchingFilters = $event" />
        <SubTabPicker :subtabs="subTabs" class="mt-10 md:mt-10 mb-6" @subtab-selected="selectedSubTab = $event">
            <component :is="activeSubTabComponent" :filters="filters" :is-fetching-filters="isFetchingFilters" @has-k-anon-error="hasKAnonError = $event" />
        </SubTabPicker>
        <InsightCard class="mt-10" :is-loading="isFetchingInsight" :content="insight?.content" />
    </div>
</template>

<script setup lang="ts">
import "chartjs-adapter-luxon";
import { useFetch } from "~vue/api";
import { useCustomLabels } from "~vue/composables/useCustomLabels";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";
import { computed, inject, markRaw, type Ref, ref, watch } from "vue";

import AnalyticsFilters from "../filters/AnalyticsFilters.vue";
import InsightCard from "../InsightCard.vue";
import SubTabPicker from "../SubTabPicker.vue";
import type { ClientAnalyticsTabConfig, NadiaInsightResponse, SelectedFilters } from "../types";
import AttritionSubTab from "./insights/AttritionSubTab.vue";
import ProductivitySubTab from "./insights/ProductivitySubTab.vue";

const getLabel = useCustomLabels("client_analytics.insights");
const tabConfig = inject<ClientAnalyticsTabConfig>("analyticsTabConfig");

const filters = ref<SelectedFilters>({});
const isFetchingFilters = ref(true);
const hasKAnonError = ref(false);

interface SubTabDef {
    id: string;
    label: string | Ref<string, string>;
    component: unknown;
    enabled: boolean;
}

const ALL_SUB_TAB_DEFS: SubTabDef[] = [
    {
        id: "attrition",
        label: getLabel("attrition_risk"),
        component: markRaw(AttritionSubTab),
        enabled: tabConfig?.insights_attrition_enabled ?? false,
    },
    {
        id: "productivity",
        label: getLabel("reduced_productivity"),
        component: markRaw(ProductivitySubTab),
        enabled: tabConfig?.insights_productivity_enabled ?? false,
    },
] as const;

const SUB_TAB_DEFS = computed(() => ALL_SUB_TAB_DEFS.filter((subTab) => subTab.enabled));

const selectedSubTab = ref<string>(SUB_TAB_DEFS.value[0]?.id || "");

const subTabs = computed(() =>
    SUB_TAB_DEFS.value.map((subTab) => ({
        id: subTab.id,
        label: subTab.label,
        isSelected: subTab.id === selectedSubTab.value,
    })),
);

const activeSubTabComponent = computed(() => {
    const subTabDef = SUB_TAB_DEFS.value.find((subTab) => subTab.id === selectedSubTab.value);
    return subTabDef ? subTabDef.component : null;
});

const { data: insight, isFetching: isFetchingInsight } = useFetch("/api/client-analytics/nadia-insights/insights", { immediate: true }).json<NadiaInsightResponse>();

watch(
    selectedSubTab,
    (newSubTab) => {
        if (newSubTab) {
            logUserInteraction(AmplitudeEventNames.CLIENT_ANALYTICS_INSIGHTS_SUBTAB_SHOWN, { sub_tab: newSubTab });
        }
    },
    { immediate: true },
);
</script>
